/**
 * InterviewQuotaBanner — Subscription quota status banner for the Interview Practice page.
 *
 * Renders directly below the "JobMockAI" header and above the stepper.
 *
 * Cases:
 *  1. Loading  — renders nothing (avoids layout shift)
 *  2. Unlimited plan (quota.limit === -1) — success Alert
 *  3. Free with interviews remaining (limit > 0 && remaining > 0) — info Alert with progress bar
 *  4. Free with quota exhausted (limit > 0 && remaining === 0) — warning Alert + Upgrade button
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  LinearProgress,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  Upgrade,
} from '@mui/icons-material';

const InterviewQuotaBanner = ({ quota, loading }) => {
  const navigate = useNavigate();

  // Don't render anything while fetching to avoid banner "flash"
  if (loading || quota === null) return null;

  // ── Case 1: Unlimited plan ──────────────────────────────────────
  if (quota.limit === -1) {
    return (
      <Box sx={{ mb: 3 }} data-testid="quota-banner-unlimited">
        <Alert
          severity="success"
          icon={<CheckCircleOutline fontSize="inherit" />}
        >
          Your plan includes <strong>unlimited</strong> practice interviews. Practice as much as you need!
        </Alert>
      </Box>
    );
  }

  // ── Case 2: Free / paid with interviews remaining ───────────────
  if (quota.remaining > 0) {
    const pct = Math.round(((quota.limit - quota.remaining) / quota.limit) * 100);
    return (
      <Box sx={{ mb: 3 }} data-testid="quota-banner-remaining">
        <Alert severity="info">
          <AlertTitle sx={{ mb: 0.5 }}>
            You have{' '}
            <strong>
              {quota.remaining} of {quota.limit}
            </strong>{' '}
            free practice interviews remaining this month.
          </AlertTitle>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ mt: 1, borderRadius: 1, height: 6 }}
            color="info"
            data-testid="quota-progress-bar"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {quota.used} used · {quota.remaining} remaining
          </Typography>
        </Alert>
      </Box>
    );
  }

  // ── Case 3: Quota exhausted ─────────────────────────────────────
  return (
    <Box sx={{ mb: 3 }} data-testid="quota-banner-exhausted">
      <Alert
        severity="warning"
        action={
          <Button
            variant="contained"
            size="small"
            color="warning"
            startIcon={<Upgrade />}
            onClick={() => navigate('/pricing')}
            data-testid="quota-upgrade-btn"
            sx={{ whiteSpace: 'nowrap', fontWeight: 600, textTransform: 'none' }}
          >
            Upgrade to Unlimited
          </Button>
        }
      >
        <AlertTitle>Monthly Limit Reached</AlertTitle>
        You have used all your free interviews for the month. Upgrade your plan to continue practicing.
      </Alert>
    </Box>
  );
};

InterviewQuotaBanner.propTypes = {
  quota: PropTypes.shape({
    limit:     PropTypes.number,
    used:      PropTypes.number,
    remaining: PropTypes.number,
  }),
  loading: PropTypes.bool,
};

export default InterviewQuotaBanner;
